import React from 'react';
import { MapNode, InnerLineageItem } from './useMapLayout';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
}

const SECTION_META = {
  origin:         { icon: '🔁', bg: 'bg-indigo-900/50', border: 'border-indigo-600', text: 'text-indigo-200', bgL: 'bg-indigo-50',  borderL: 'border-indigo-300', textL: 'text-indigo-800' },
  tag:            { icon: '🏷',  bg: 'bg-gray-800/60',   border: 'border-gray-600',   text: 'text-gray-200',   bgL: 'bg-gray-50',    borderL: 'border-gray-300',   textL: 'text-gray-700'   },
  suggested_name: { icon: '✨',  bg: 'bg-gray-800/60',   border: 'border-gray-600',   text: 'text-gray-300',   bgL: 'bg-gray-50',    borderL: 'border-gray-200',   textL: 'text-gray-600'   },
} as const;

/**
 * Full HTML overlay for lineage_cluster at high zoom (> LOD_NODES).
 * Renders each inner lineage item as a card:
 *   - origin: indigo bar with full origin ID + remix depth
 *   - tag: colored pill matching tag category color
 *   - suggested_name: gray pill with name
 * pointer-events: none — read-only display.
 */
const LineageClusterOverlay: React.FC<Props> = ({
  node,
  transform,
  canvasWidth,
  canvasHeight,
  darkMode,
}) => {
  if (!node.innerLineage?.length) return null;

  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  const { sx: cx, sy: cy } = toScreen(node.x, node.y);
  const screenR = node.radius * transform.scale;

  // Viewport cull
  if (
    cx + screenR < 0 ||
    cx - screenR > canvasWidth ||
    cy + screenR < 0 ||
    cy - screenR > canvasHeight
  ) return null;

  const clusterData = node.data as {
    tags: string[];
    suggestedNames: string[];
    originId: string | null;
    remixChain: string[];
  };
  const remixDepth = node.remixDepth ?? 0;

  return (
    <>
      {node.innerLineage.map((item) => {
        const wx = node.x + item.dx;
        const wy = node.y + item.dy;
        const { sx, sy } = toScreen(wx, wy);
        const meta = SECTION_META[item.section];

        const cardW =
          item.section === 'origin'
            ? Math.max(120, Math.min(200, node.radius * transform.scale * 1.1))
            : item.section === 'tag'
              ? Math.max(50, Math.min(100, node.radius * transform.scale * 0.65))
              : Math.max(80, Math.min(140, node.radius * transform.scale * 0.85));

        return (
          <div
            key={item.id}
            style={{
              position: 'absolute',
              left: sx - cardW / 2,
              top: sy - (item.section === 'origin' ? 16 : 12),
              width: cardW,
              pointerEvents: 'none',
              zIndex: 20,
            }}
          >
            {item.section === 'origin' ? (
              <div
                className={`rounded border shadow-md px-2 py-1 flex flex-col gap-0.5 ${
                  darkMode
                    ? `${meta.bg} ${meta.border}`
                    : `${meta.bgL} ${meta.borderL}`
                }`}
              >
                <div className="flex items-center gap-1 min-w-0">
                  <span style={{ fontSize: 9 }}>{meta.icon}</span>
                  <span
                    className={`text-xs font-bold uppercase tracking-wide flex-shrink-0 ${
                      darkMode ? meta.text : meta.textL
                    }`}
                    style={{ fontSize: 7 }}
                  >
                    Remixed from
                  </span>
                  {remixDepth > 0 && (
                    <span
                      className="ml-auto flex-shrink-0 px-1 rounded-full bg-indigo-500 text-white font-bold"
                      style={{ fontSize: 6 }}
                    >
                      depth {remixDepth}
                    </span>
                  )}
                </div>
                <p
                  className={`font-mono truncate ${darkMode ? 'text-indigo-200' : 'text-indigo-700'}`}
                  style={{ fontSize: 8 }}
                >
                  {clusterData.originId}
                </p>
              </div>
            ) : item.section === 'tag' ? (
              <div
                className="rounded-full border px-1.5 py-0.5 flex items-center gap-0.5 shadow-sm"
                style={{
                  backgroundColor: item.tagColor ? `${item.tagColor}22` : darkMode ? '#374151' : '#f3f4f6',
                  borderColor:     item.tagColor ? `${item.tagColor}88` : darkMode ? '#4b5563' : '#d1d5db',
                }}
              >
                <span style={{ fontSize: 7 }}>{meta.icon}</span>
                <span
                  style={{ fontSize: 8, color: item.tagColor ?? (darkMode ? '#d1d5db' : '#374151') }}
                >
                  {item.label}
                </span>
              </div>
            ) : (
              <div
                className={`rounded border px-1.5 py-0.5 flex items-center gap-0.5 shadow-sm ${
                  darkMode
                    ? `${meta.bg} ${meta.border}`
                    : `${meta.bgL} ${meta.borderL}`
                }`}
              >
                <span style={{ fontSize: 7 }}>{meta.icon}</span>
                <span
                  className={`truncate ${darkMode ? meta.text : meta.textL}`}
                  style={{ fontSize: 8 }}
                >
                  {item.label}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default LineageClusterOverlay;
